import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "../buttons/IconButton";
import { classNames } from "../styles/classNames";

interface CollapsibleColumnProps {
  /** The column's short name, shown on its rail and in the buttons' labels. */
  label: string;
  /** Which edge of the page the column sits on; the arrows point that way. */
  side: "left" | "right";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The column's place in the page's grid, open or closed (span, stickiness). */
  className?: string;
  /** The open column's frame (card, padding, scrolling); no display class, `hidden` closes it. */
  contentClassName?: string;
  /** The closed rail's layout; a vertical rail sets its flex direction here. */
  railClassName?: string;
  /** The rail's name; a vertical rail turns it here (`[writing-mode:vertical-rl]`). */
  railLabelClassName?: string;
  children: ReactNode;
}

/**
 * A page column the user can close to a narrow rail and open again (web-app.md WA-1). The
 * content stays mounted while closed, so a half-written comment survives. Focus moves to
 * the button that undoes the toggle. Keep the open state with `useStoredOpen`.
 */
export default function CollapsibleColumn({
  label,
  side,
  open,
  onOpenChange,
  className,
  contentClassName,
  railClassName,
  railLabelClassName,
  children,
}: CollapsibleColumnProps) {
  const { t } = useTranslation();
  const contentId = useId();
  const TowardsEdge = side === "left" ? ChevronDoubleLeftIcon : ChevronDoubleRightIcon;
  const AwayFromEdge = side === "left" ? ChevronDoubleRightIcon : ChevronDoubleLeftIcon;
  const railButton = useRef<HTMLButtonElement>(null);
  const hideButton = useRef<HTMLButtonElement>(null);
  // Only the column's own buttons move focus; the stored state on page load and keys don't.
  const toggled = useRef(false);
  const toggle = (next: boolean) => {
    toggled.current = true;
    onOpenChange(next);
  };

  useEffect(() => {
    if (toggled.current) {
      toggled.current = false;
      (open ? hideButton : railButton).current?.focus();
    }
  }, [open]);

  return (
    <div className={className}>
      {open ? null : (
        <button
          ref={railButton}
          type="button"
          className={classNames(
            "app-card app-column-rail flex w-full items-center gap-2 px-2 py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent)]",
            railClassName,
          )}
          aria-expanded={false}
          aria-controls={contentId}
          aria-label={t("shell.column.show", { column: label })}
          title={t("shell.column.show", { column: label })}
          onClick={() => { toggle(true); }}
        >
          <AwayFromEdge className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className={railLabelClassName}>{label}</span>
        </button>
      )}
      <div id={contentId} hidden={!open} className={contentClassName}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="app-text-muted text-xs font-semibold">{label}</span>
          <IconButton
            ref={hideButton}
            label={t("shell.column.hide", { column: label })}
            title={t("shell.column.hide", { column: label })}
            aria-expanded={true}
            aria-controls={contentId}
            className="h-7 w-7 shrink-0 p-1"
            onClick={() => { toggle(false); }}
          >
            <TowardsEdge className="h-4 w-4" />
          </IconButton>
        </div>
        {children}
      </div>
    </div>
  );
}
